import reading from './kalverCornerRows.json';
import type { BuildContext } from '../../types';
import type { PolygonIndex } from '../../../geo/polygonIndex';
import type { Vec2 } from '../../../geo/types';
import { difference, intersection } from '../../../geo/clip';
import { polygonArea } from '../../../geo/polygon';
import { footprintInside, lotsOverlap, obliqueCorners } from '../lotFootprint';
import { measureLine } from '../measuredLine';
import { LOT } from '../lotRules';
import type { Lot, ReservedFootprint } from '../types';

export const KALVER_CORNER = reading;

/** Each named property keeps its fitted frontage; a failed fit stays unresolved. */
export function planKalverCorner(
  ctx: Pick<BuildContext, 'layout' | 'site'>,
  buildable: PolygonIndex,
  reserved: readonly ReservedFootprint[]
) {
  if (
    ['street-sint-luciensteeg-2', 'street-kalverstraat'].some(
      (id) => !ctx.layout.streets.some((s) => s.id === id)
    )
  )
    return { lots: [], reserved: [], unresolved: [] };
  const lots: Lot[] = [],
    unresolved: string[] = [];
  reading.rows.forEach((row, i) => {
    const street = ctx.layout.streets.find((s) => s.id === row.source)!,
      line = measureLine(
        street.centerline,
        street.centerline.map(() => street.width)
      ),
      total = row.houses.reduce((n, h) => n + h.widthWeight, 0);
    let at = row.from;
    row.houses.forEach((house, j) => {
      const span = ((row.to - row.from) * house.widthWeight) / total,
        { point, tangent } = line.pointAt(at + span / 2),
        inward: Vec2 = row.side === 'left' ? [-tangent[1], tangent[0]] : [tangent[1], -tangent[0]],
        facing: Vec2 = [-inward[0], -inward[1]],
        right: Vec2 = [-facing[1], facing[0]],
        front: Vec2 = [
          point[0] + inward[0] * (street.width / 2 + LOT.sourceOffset),
          point[1] + inward[1] * (street.width / 2 + LOT.sourceOffset),
        ],
        corners = obliqueCorners(front, facing, span - 0.08, row.depth, row.shear),
        lot: Lot = {
          index: lots.length,
          sourceId: street.id,
          side: row.side as 'left' | 'right',
          ordinal: 52_000 + i * 100 + j,
          kind: 'house',
          facing,
          depth: row.depth,
          frontage: span - 0.08,
          corners,
          obb: {
            center: [
              front[0] - (facing[0] * row.depth) / 2 + (right[0] * row.shear * row.depth) / 2,
              front[1] - (facing[1] * row.depth) / 2 + (right[1] * row.shear * row.depth) / 2,
            ],
            axisU: right,
            halfU: (span - 0.08 + Math.abs(row.shear) * row.depth) / 2,
            halfV: row.depth / 2,
          },
          plot: { frontMid: front, shear: row.shear },
          reconstruction: {
            id: house.id,
            row: row.id,
            evidence: 'inferred',
            params: {
              storeys: 1,
              storeyHeight: row.storeyHeight,
              pitch: 1.1,
              roofMaterial: 'tile',
              frontGable: 'plain',
              backGable: 'plain',
              chimneys: [],
            },
          },
        };
      at += span;
      if (
        row.to > line.length ||
        !footprintInside(buildable, corners) ||
        lots.some((other) => lotsOverlap(lot, other, 0.001)) ||
        reserved.some((r) =>
          intersection([{ outer: [...corners], holes: [] }], [r.polygon]).some(
            (p) => polygonArea(p) > 0.001
          )
        )
      ) {
        unresolved.push(house.id);
        return;
      }
      lots.push(lot);
    });
  });
  const plots: ReservedFootprint[] = lots.map((lot) => ({
    id: `surveyed:${lot.reconstruction!.id}`,
    polygon: { outer: [...lot.corners], holes: [] },
  }));
  if (reading.privateGround.length) {
    const ground = { outer: reading.privateGround.map((p) => [p[0]!, p[1]!] as Vec2), holes: [] };
    const dry = difference(
      [ground],
      [...ctx.site.water, ...ctx.site.streets, ...reserved.map((r) => r.polygon)]
    );
    plots.push(...dry.map((polygon, i) => ({ id: `kalver-corner-private-ground-${i}`, polygon })));
  }
  return { lots, reserved: plots, unresolved };
}
